import { BrowserService } from 'app/modules/social/services/browser.service';
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  HostBinding,
  Input,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { AppState } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { cleanTracks } from 'app/utils/media/stream';

import { StageSizeType } from '../../performer-participant/performer-participant.types';
import { ShareScreenParticipantService } from '../../services/share-screen/share-screen-participant.service';
import { SideSectionService } from '../../services/side-section.service';
import { PopOutState, socialAppActions, socialAppSelectors } from '../../state/app';

const DEFAULT_ZOOM = 1;
const ZOOM_MOD = 0.5;

@Component({
  selector: 'app-presentation-presenter-sharing',
  templateUrl: './presentation-presenter-sharing.component.html',
  styleUrls: ['./presentation-presenter-sharing.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationPresenterSharingComponent extends Loggable implements OnInit, AfterViewInit, OnDestroy {
  @Input() @HostBinding('class.screenshare') public isSharingScreen = false;
  @Input() @HostBinding('class.captions-open') public isCaptionsOpen = false;
  @Input() @HostBinding('class.drawer-open') public isGalleryDrawerOpen = false;
  @Input() public isPresentersDrawerOpen = true;

  public isPresenting = false;
  public screenStream?: MediaStream;
  public isHost = false;
  public requireFullScreen = false;
  protected popOutVideo = false;
  protected isVideoPlaying = false;
  protected isPopout: PopOutState = { mode: 'SCREENSHARE', open: false };
  public presentationV3 = false;

  // Zoom In screen share
  public screenShareZoom = false;
  protected zoomScale = DEFAULT_ZOOM;
  protected isDraging = false;
  protected startX = 1;
  protected startY = 1;
  protected dragX = 1;
  protected dragY = 1;
  protected screenOffsetX = 1;
  protected screenOffsetY = 1;

  private static instance?: PresentationPresenterSharingComponent;
  private isUserSharingScreen = false;

  protected isFullScreenMobile = false;
  protected isMobile = false;

  public static getInstance(): PresentationPresenterSharingComponent | undefined {
    return this.instance;
  }

  public set viewSize(value: StageSizeType) {
    this._viewSize = value;
    this.shareScreenParticipantService.viewSize = value;
  }

  public get viewSize(): StageSizeType {
    return this._viewSize;
  }

  private _viewSize = StageSizeType.MEDIUM;
  private destroyed$ = new ReplaySubject<boolean>(1);

  @ViewChild('videoElement', { static: true }) private videoElement?: ElementRef<HTMLVideoElement>;
  @ViewChild('videoWrapper', { static: true }) private videoWrapper?: ElementRef<HTMLDivElement>;

  public constructor(
    public sideSectionService: SideSectionService,
    private shareScreenParticipantService: ShareScreenParticipantService,
    private store: Store<AppState>,
    private browserService: BrowserService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.setDisplayName('ShareScreenParticipantComponent');

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresenting) => {
        this.isPresenting = isPresenting;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isUserSharingScreen)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserSharingScreen) => {
        this.isUserSharingScreen = isUserSharingScreen;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => {
        this.isHost = isHost;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isPopOut)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPopOut) => {
        this.isPopout = isPopOut;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_v3) => {
        this.presentationV3 = presentation_v3;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.screenshare_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((screenshare_zoom) => {
        this.screenShareZoom = screenshare_zoom;
        this.changeDetectorRef.markForCheck();
      });

    PresentationPresenterSharingComponent.instance = this;
  }

  private handleMediaStreamChange({ id, stream }: { stream?: MediaStream; id: string }): void {
    assertDefined(this.videoElement);
    const { nativeElement: videoElement } = this.videoElement;

    if (videoElement.getAttribute('data-stream-id') === id) {
      this.error('handleMediaStreamChange: skipping video stream update, stream already set as source');
      return;
    }
    videoElement.srcObject = stream || null;
    videoElement.setAttribute('data-stream-id', id);
    // mute if ScreenSharing
    if (this.isUserSharingScreen) {
      videoElement.muted = true;
    }
    this.info('handleMediaStreamChange: ', id, stream);
  }

  public get stageSizeType(): typeof StageSizeType {
    return StageSizeType;
  }

  public ngOnInit(): void {
    // Listen for share media streams
    this.shareScreenParticipantService.mediaStream$.pipe(takeUntil(this.destroyed$)).subscribe(({ id, stream }) => {
      this.handleMediaStreamChange({ id, stream: stream || undefined });
      this.changeDetectorRef.markForCheck();
    });
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.pop_out_video)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((popOutVideo) => {
        this.popOutVideo = popOutVideo;
        this.changeDetectorRef.markForCheck();
      });

    this.isMobile = this.browserService.isMobile();
  }

  public ngAfterViewInit(): void {
    // Add listener for fullscreen events on mobile
    if (this.isMobile) {
      document.addEventListener('fullscreenchange', this.onFullScreenChange.bind(this));
    }
  }

  @HostBinding('class.is-fullscreen')
  public get isFullScreen(): boolean {
    return this.viewSize === StageSizeType.FULL_SCREEN;
  }

  public toggleFullScreen(force?: StageSizeType): void {
    this.viewSize = force ? force : this.isFullScreen ? StageSizeType.MEDIUM : StageSizeType.FULL_SCREEN;
    this.store.dispatch(socialAppActions.SET_PRESENTATION_STAGE_SIZE({ stageSize: this.viewSize }));
  }

  public endScreenSharing(): void {
    this.isVideoPlaying = false;
    this.toggleFullScreen(StageSizeType.MEDIUM);
    this.shareScreenParticipantService.endScreenSharing().catch((e) => {
      this.error('endScreenSharing: Failed to end screen sharing', e);
    });
  }

  protected startDrag(event: MouseEvent) {
    if (!this.isZoomedIn || !this.screenShareZoom) {
      return;
    }
    this.isDraging = true;
    this.startX = event.clientX - this.screenOffsetX;
    this.startY = event.clientY - this.screenOffsetY;
  }

  protected endDrag(event: MouseEvent) {
    if (!this.screenShareZoom) {
      return;
    }
    this.isDraging = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
  }

  protected dragScreenShare(event: MouseEvent) {
    if (this.isZoomedIn && this.isDraging) {
      this.dragX = event.clientX - this.startX;
      this.dragY = event.clientY - this.startY;
      this.adjustToBounds();
    }
  }

  protected leaveDraggableArea() {
    if (this.isDraging) {
      this.isDraging = false;
    }
  }

  protected controlZoom(action: 'in' | 'out' | 'reset') {
    if (action === 'in') {
      this.zoomIn();
    } else if (action === 'out') {
      this.zoomOut();
    } else if (action === 'reset') {
      this.resetToDefaultZoom();
    }
  }

  public get isZoomedIn() {
    return this.zoomScale > DEFAULT_ZOOM;
  }

  private zoomIn() {
    this.zoomScale += ZOOM_MOD;
  }

  private zoomOut() {
    if (!this.isZoomedIn) {
      return;
    }
    this.zoomScale -= ZOOM_MOD;
    // reset position if it is the last zoom out
    if (!this.isZoomedIn) {
      this.screenOffsetX = 1;
      this.screenOffsetY = 1;
      return;
    }
    this.screenOffsetX -= this.screenOffsetX * ZOOM_MOD;
    this.screenOffsetY -= this.screenOffsetY * ZOOM_MOD;
  }

  private resetToDefaultZoom() {
    this.zoomScale = DEFAULT_ZOOM;
    this.startX = 1;
    this.startY = 1;
    this.dragX = 1;
    this.dragY = 1;
    this.screenOffsetX = 1;
    this.screenOffsetY = 1;
  }

  private adjustToBounds() {
    const containerRect = this.videoWrapper?.nativeElement.getBoundingClientRect();
    const contentRect = this.videoElement?.nativeElement.getBoundingClientRect();

    if (!containerRect || !contentRect) {
      return;
    }

    const maxOffsetX = (contentRect.width - containerRect.width) / 2;
    const maxOffsetY = (contentRect.height - containerRect.height) / 2;

    this.screenOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, this.dragX));
    this.screenOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, this.dragY));
  }

  public get videoStyles() {
    const cursor = this.isDraging ? 'grabbing' : this.isZoomedIn ? 'grab' : 'auto';
    const transform = `translate(${this.screenOffsetX}px, ${this.screenOffsetY}px) scale(${this.zoomScale})`;
    const transition = !this.isDraging ? 'transform 0.3s ease' : '';
    return { transform, cursor, transition };
  }

  public get isScreenPopout(): boolean {
    return this.isPopout.mode === 'SCREENSHARE' && this.isPopout.open;
  }

  public handleFullscreenChange() {
    const videoWrapperElement = this.videoWrapper?.nativeElement;
    assertDefined(videoWrapperElement);

    if (this.isFullScreenMobile) {
      if (document.fullscreenElement === videoWrapperElement) {
        document.exitFullscreen().catch((e) => {
          this.error('handleFullScreen: Failed to toggle OFF fullscreen on video', e);
        });

        this.isFullScreenMobile = false;
      }
    } else {
      videoWrapperElement.requestFullscreen().catch((e) => {
        this.error('handleFullScreen: Failed to toggle ON fullscreen on video', e);
      });
      this.isFullScreenMobile = true;
    }
  }

  private onFullScreenChange() {
    if (!document.fullscreenElement) {
      //Event to turn off fullscreen flag if fullscreen disabled by external events
      this.isFullScreenMobile = false;
    }
  }

  public get isFullScreenEnabled() {
    return !!document.fullscreenEnabled && this.isMobile && !!this.videoElement?.nativeElement;
  }

  public async ngOnDestroy(): Promise<void> {
    this.toggleFullScreen(StageSizeType.MEDIUM);

    if (this.isFullScreenMobile) {
      //Turn off fullscreen
      this.handleFullscreenChange();
      this.isFullScreenMobile = false;
    }
    document.removeEventListener('fullscreenchange', this.onFullScreenChange.bind(this));

    assertDefined(this.videoElement?.nativeElement);
    this.videoElement.nativeElement.srcObject = null;
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.viewSize = StageSizeType.MEDIUM;
    PresentationPresenterSharingComponent.instance = undefined;

    if (document.pictureInPictureEnabled) {
      document.exitPictureInPicture().catch((err) => new Error(err));
    }

    await this.shareScreenParticipantService.destroy();
    await cleanTracks(this.screenStream, this);
  }
}
